import { useState, useMemo } from "react";

import {
  useGetProductsInfinite,
  useSearchProducts,
} from "@/_services/hooks/invoice.hooks";
import { Product } from "@/_services/invoiceService";

export interface SelectedProduct {
  selected: boolean;
  quantity: number;
}

// Default product that appears first in the list
const DEFAULT_PRODUCT: Product = {
  id: -2, // Use negative ID to avoid conflicts with real products
  label: "Frais de reprise de Dossier de Diagnostics Techniques",
  description:
    "Frais administratifs pour la reprise d'un dossier de diagnostics techniques existant",
  price: "0",
  price_before_tax: "0",
  currency: "EUR",
  unit: "forfait",
  reference: "0.REP",
  vat_rate: "FR_200",
  external_reference: "0.REP",
  ledger_account: null,
};

export const useProductManagement = () => {
  const [selectedProducts, setSelectedProducts] = useState<
    Record<string, SelectedProduct>
  >({});
  const [productSearchQuery, setProductSearchQuery] = useState("");
  const {
    data: productsData,
    isLoading: loadingProducts,
    isFetchingNextPage: loadingMoreProducts,
    hasNextPage: hasMoreProducts,
    fetchNextPage,
    error: productsError,
  } = useGetProductsInfinite({ limit: 50 });

  const { data: searchResults = [] } = useSearchProducts(productSearchQuery, {
    enabled: productSearchQuery.trim().length >= 1,
  });

  const products = useMemo(() => {
    if (!productsData) return [DEFAULT_PRODUCT];
    const apiProducts = productsData.pages.flatMap((page) => page.items);
    return [DEFAULT_PRODUCT, ...apiProducts];
  }, [productsData]);

  const filteredProducts = useMemo(() => {
    if (productSearchQuery.trim().length >= 1) {
      const matchesDefault =
        DEFAULT_PRODUCT.label
          .toLowerCase()
          .includes(productSearchQuery.toLowerCase()) ||
        DEFAULT_PRODUCT.description
          .toLowerCase()
          .includes(productSearchQuery.toLowerCase()) ||
        DEFAULT_PRODUCT.reference
          .toLowerCase()
          .includes(productSearchQuery.toLowerCase());

      return matchesDefault
        ? [DEFAULT_PRODUCT, ...searchResults]
        : searchResults;
    }

    return products;
  }, [products, searchResults, productSearchQuery]);

  const error = productsError ? "Erreur lors du chargement des produits" : null;

  const handleProductToggle = (productId: string) => {
    setSelectedProducts((prev) => ({
      ...prev,
      [productId]: prev[productId]?.selected
        ? { ...prev[productId], selected: false }
        : { selected: true, quantity: 1 },
    }));
  };

  const handleQuantityChange = (productId: string, quantity: number) => {
    if (quantity < 1) return;
    setSelectedProducts((prev) => ({
      ...prev,
      [productId]: {
        ...prev[productId],
        quantity,
      },
    }));
  };

  const handleResetProductSelection = () => {
    setSelectedProducts({});
  };

  const handleFetchMoreProducts = () => {
    if (hasMoreProducts && !loadingMoreProducts) {
      fetchNextPage();
    }
  };

  const getSelectedProductsCount = () => {
    return Object.values(selectedProducts).filter((p) => p.selected).length;
  };

  const calculateTotal = () => {
    return Object.entries(selectedProducts)
      .filter(([_, product]) => product.selected)
      .reduce((total, [productId, product]) => {
        let productData;
        if (productId === DEFAULT_PRODUCT.id.toString()) {
          productData = DEFAULT_PRODUCT;
        } else {
          productData = products.find((p) => p.id.toString() === productId);
        }
        return total + parseFloat(productData?.price || "0") * product.quantity;
      }, 0);
  };

  const getSelectedProductsData = () => {
    return Object.entries(selectedProducts)
      .filter(([_, product]) => product.selected)
      .map(([productId, product]) => {
        let productData;
        if (productId === DEFAULT_PRODUCT.id.toString()) {
          productData = DEFAULT_PRODUCT;
        } else {
          productData = products.find((p) => p.id.toString() === productId);
        }
        return {
          id: productId,
          name: productData?.label,
          price: parseFloat(productData?.price || "0"),
          quantity: product.quantity,
          total: parseFloat(productData?.price || "0") * product.quantity,
        };
      });
  };

  return {
    // State
    products,
    selectedProducts,
    filteredProducts,
    hasMoreProducts,
    loadingProducts,
    loadingMoreProducts,
    productSearchQuery,
    error,

    // Actions
    setProductSearchQuery,
    handleProductToggle,
    handleQuantityChange,
    handleResetProductSelection,
    handleFetchMoreProducts,

    // Computed values
    getSelectedProductsCount,
    calculateTotal,
    getSelectedProductsData,
  };
};
